import { useState, useEffect } from 'react';
import { jobsApi } from '@/lib/api';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { FileText, Download, Eye, Search, Filter, Image, File, FileSpreadsheet } from 'lucide-react';
import { format } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

const fileTypeIcons = {
  'image': Image,
  'pdf': FileText,
  'excel': FileSpreadsheet,
  'default': File,
};

const getFileIcon = (filename) => {
  if (!filename) return File;
  const ext = filename.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return Image;
  if (ext === 'pdf') return FileText;
  if (['xls', 'xlsx', 'csv'].includes(ext)) return FileSpreadsheet;
  return File;
};

const formatFileSize = (bytes) => {
  if (!bytes || isNaN(bytes)) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export default function DocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterJob, setFilterJob] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const jobsData = await jobsApi.getAll();
      setJobs(jobsData);
      
      // Extract all files from all jobs
      const allDocs = [];
      jobsData.forEach(job => {
        if (job.files && Array.isArray(job.files)) {
          job.files.forEach(file => {
            allDocs.push({
              ...file,
              job_id: job.id,
              job_number: job.job_number,
              job_title: job.title,
              property_address: job.property_address,
            });
          });
        }
      });
      
      // Sort by upload date (newest first)
      allDocs.sort((a, b) => new Date(b.uploaded_at || 0) - new Date(a.uploaded_at || 0));
      setDocuments(allDocs);
    } catch (error) {
      console.error('Failed to load documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredDocs = documents.filter(doc => {
    // Search filter
    const searchLower = searchTerm.toLowerCase();
    const searchMatch = !searchTerm || 
      (doc.name || '').toLowerCase().includes(searchLower) ||
      (doc.job_number || '').toLowerCase().includes(searchLower) ||
      (doc.property_address || '').toLowerCase().includes(searchLower);
    
    // Type filter
    let typeMatch = filterType === 'all';
    if (!typeMatch && doc.name) {
      const ext = (doc.name.split('.').pop() || '').toLowerCase();
      if (filterType === 'images' && ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) typeMatch = true;
      if (filterType === 'pdfs' && ext === 'pdf') typeMatch = true;
      if (filterType === 'documents' && ['doc', 'docx', 'txt'].includes(ext)) typeMatch = true;
      if (filterType === 'spreadsheets' && ['xls', 'xlsx', 'csv'].includes(ext)) typeMatch = true;
    }
    
    // Job filter
    const jobMatch = filterJob === 'all' || doc.job_id === filterJob;
    
    return searchMatch && typeMatch && jobMatch;
  });

  const totalSize = documents.reduce((sum, doc) => sum + (doc.size || 0), 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F7A78]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Documents</h1>
          <p className="text-sm text-stone-500 mt-1">
            {documents.length} files • {formatFileSize(totalSize)} total
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-stone-400" />
                <Input
                  placeholder="Search files..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-documents"
                />
              </div>
            </div>
            
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[160px]" data-testid="filter-type">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="File type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="images">Images</SelectItem>
                <SelectItem value="pdfs">PDFs</SelectItem>
                <SelectItem value="documents">Documents</SelectItem>
                <SelectItem value="spreadsheets">Spreadsheets</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filterJob} onValueChange={setFilterJob}>
              <SelectTrigger className="w-[200px]" data-testid="filter-job">
                <SelectValue placeholder="Filter by job" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Jobs</SelectItem>
                {jobs.map(job => (
                  <SelectItem key={job.id} value={job.id}>
                    {job.job_number} - {job.title?.substring(0, 20)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Documents Grid */}
      {filteredDocs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-stone-500">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No documents found</p>
            {searchTerm && <p className="text-sm mt-1">Try adjusting your search</p>}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredDocs.map((doc, idx) => {
            const FileIcon = getFileIcon(doc.name);
            const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(
              doc.name?.split('.').pop()?.toLowerCase()
            );
            
            return (
              <Card key={`${doc.job_id}-${idx}`} className="hover:shadow-md transition-shadow" data-testid={`doc-card-${idx}`}>
                <CardContent className="p-4">
                  {/* Thumbnail or Icon */}
                  <div className="h-32 bg-stone-100 rounded-lg flex items-center justify-center mb-3 overflow-hidden">
                    {isImage && doc.url ? (
                      <img 
                        src={doc.url.startsWith('http') ? doc.url : `${API}${doc.url}`} 
                        alt={doc.name}
                        className="h-full w-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <FileIcon className="h-12 w-12 text-stone-400" />
                    )}
                  </div>
                  
                  {/* File Info */}
                  <div className="space-y-1">
                    <h4 className="font-medium text-sm truncate" title={doc.name}>
                      {doc.name || 'Unnamed file'}
                    </h4>
                    <p className="text-xs text-stone-500">
                      {doc.job_number && (
                        <span className="bg-stone-100 px-2 py-0.5 rounded mr-2">
                          {doc.job_number}
                        </span>
                      )}
                      {formatFileSize(doc.size)}
                    </p>
                    {doc.uploaded_at && (
                      <p className="text-xs text-stone-400">
                        {format(new Date(doc.uploaded_at), 'dd MMM yyyy')}
                      </p>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2 mt-3">
                    {doc.url && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={() => window.open(doc.url.startsWith('http') ? doc.url : `${API}${doc.url}`, '_blank')}
                        >
                          <Eye className="h-3 w-3 mr-1" /> View
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          asChild
                        >
                          <a href={doc.url.startsWith('http') ? doc.url : `${API}${doc.url}`} download={doc.name}>
                            <Download className="h-3 w-3 mr-1" /> Download
                          </a>
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
